import React, { PropsWithChildren } from "react";

import { css, cx } from "@emotion/css";

import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { PaletteColors } from "@/theme/PaletteOverrides.ts";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";

export type BaseColoredCardProps = PropsWithChildren<{
  ref?: React.Ref<HTMLDivElement>;
  color: PaletteColors;
  border?: boolean;
  standout?: boolean;
  square?: boolean;
}>;

export type ColoredCardProps = AtomProps<BaseColoredCardProps, StyleClasses<"root">>;

export const ColoredCard: React.FC<ColoredCardProps> = ({
  id,
  color,
  square = false,
  border = false,
  standout = false,
  className,
  classes,
  children,
  ...props
}) => {
  const styles = useStyles(makeStyles, color);

  return (
    <Card
      id={id}
      className={cx(
        {
          [styles.root]: true,
          [styles.square]: square,
          [styles.border]: border,
          [styles.standout]: standout,
        },
        classes?.root,
        className,
      )}
      {...props}
    >
      {children}
    </Card>
  );
};

const makeStyles = (theme: Theme, color: PaletteColors) => ({
  root: css({
    borderRadius: theme.spacing(4),
    backgroundColor: theme.palette[color].contrastText,
  }),
  square: css({
    borderRadius: theme.spacing(2),
  }),
  border: css({
    border: `1px solid var(--mui-palette-divider)`,
  }),
  standout: css({
    border: `${theme.spacing(0.1)} solid ${theme.palette[color].dark}`,
  }),
});
